import fs from 'node:fs';
import { execFileSync } from 'node:child_process';
import chalk from 'chalk';
import { logger } from '../utils/logger.js';

const ARTIFACT_DEPLOY_REMOTE_NAME = 'origin';

/** Check if the current directory is a Standard Agents project. */
export function detectProject(): boolean {
  const hasAgentsDir = fs.existsSync('agents');
  const hasViteConfig = ['vite.config.ts', 'vite.config.js', 'vite.config.mts', 'vite.config.mjs']
    .some((file) => fs.existsSync(file));
  return hasAgentsDir && hasViteConfig;
}

function normalizeGitRemoteHost(remote: string): string | null {
  try {
    return new URL(remote).hostname.toLowerCase();
  } catch {
    return null;
  }
}

export function isStandardAgentsArtifactRemote(remote: string): boolean {
  const host = normalizeGitRemoteHost(remote);
  if (!host) return false;
  return host === 'standardagents.ai' ||
    host.endsWith('.standardagents.ai') ||
    host === 'standardagentbuilder.com' ||
    host.endsWith('.standardagentbuilder.com') ||
    host === 'artifacts.cloudflare.net' ||
    host.endsWith('.artifacts.cloudflare.net') ||
    host === 'artifacts.cloudflare.com' ||
    host.endsWith('.artifacts.cloudflare.com') ||
    host === 'git.cloudflare.com';
}

export interface ArtifactGitDeployTarget {
  remoteName: string;
  remoteUrl: string;
  branch: string;
  refspec: string;
}

type GitOutputRunner = (args: string[]) => string;

function gitOutput(args: string[]): string {
  return execFileSync('git', args, {
    encoding: 'utf-8',
    stdio: ['ignore', 'pipe', 'pipe'],
  }).trim();
}

export function resolveArtifactGitDeployTarget(
  runner: GitOutputRunner = gitOutput,
  remoteName = ARTIFACT_DEPLOY_REMOTE_NAME,
): ArtifactGitDeployTarget {
  const remoteUrl = runner(['remote', 'get-url', remoteName]).trim();
  if (!isStandardAgentsArtifactRemote(remoteUrl)) {
    throw new Error(
      `Remote "${remoteName}" is not a Standard Agents Artifacts remote. ` +
      'Deployments now run from pushes to platform-owned repositories.',
    );
  }

  const branch = runner(['rev-parse', '--abbrev-ref', 'HEAD']).trim();
  if (!branch || branch === 'HEAD') {
    throw new Error('Cannot deploy from a detached HEAD. Check out a branch before pushing.');
  }

  return {
    remoteName,
    remoteUrl,
    branch,
    refspec: `HEAD:${branch}`,
  };
}

function pushArtifactRemote(target: ArtifactGitDeployTarget): void {
  execFileSync('git', ['push', target.remoteName, target.refspec], {
    stdio: 'inherit',
  });
}

export async function deploy(): Promise<void> {
  console.log('');
  logger.info(`${chalk.bold('Standard Agents Deploy')}\n`);

  let target: ArtifactGitDeployTarget;
  try {
    target = resolveArtifactGitDeployTarget();
  } catch (err) {
    logger.error((err as Error).message);
    logger.info('Run this command from a clone created by Standard Agents, or push to the platform git remote manually.');
    process.exit(1);
  }

  logger.info('Deployments build on the Standard Agents platform from Artifacts git pushes.');
  logger.log(`Remote: ${target.remoteUrl}`);
  logger.log(`Branch: ${target.branch}`);
  logger.log('');
  pushArtifactRemote(target);
  logger.success('Push complete. The platform build runner will deploy this commit from the Artifacts push event.');
}
